import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { Entity } from '@backstage/catalog-model';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { GiteaOrgPickerExtension } from './components/scaffolder/GiteaOrgPicker';
import { HumanitecProjectPickerExtension } from './components/scaffolder/HumanitecProjectPicker';
import { UnregisteredAgentPickerExtension } from './components/scaffolder/UnregisteredAgentPicker';
import { HelmValuesEditorExtension } from './components/scaffolder/HelmValuesEditor';
import { SystemOwnerFieldExtension } from './components/scaffolder/SystemOwnerField';
import { ArgocdProjectPickerExtension } from './components/scaffolder/ArgocdProjectPicker';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage, SettingsLayout, UserSettingsGeneral, UserSettingsAuthProviders, UserSettingsFeatureFlags } from '@backstage/plugin-user-settings';
import { PlatformStudioPage } from '@internal/plugin-platform-studio';
import { agentRegistryPlugin, AgentRegistryPage } from '@internal/plugin-agent-registry';
import { toolsRegistryPlugin, ToolsRegistryPage } from '@internal/plugin-tools-registry';
import { OnboardingWizardPage } from '@internal/plugin-onboarding-wizard';
import { workflowComposerPlugin, WorkflowComposerPage } from '@internal/plugin-workflow-composer';
import { OnboardingGate } from './components/onboarding/OnboardingGate';
import { QualityStudioPage } from '@internal/plugin-quality-studio';
import { RefreshIntervalCard } from './components/settings/RefreshIntervalCard';
import { AgentRegistryProviderCard } from './components/settings/AgentRegistryProviderCard';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { RoleProvider, useRoleContext } from './components/GlobalHeader';

import {
  AlertDisplay,
  OAuthRequestDialog,
  Progress,
} from '@backstage/core-components';
import { KyndrylSignInPage } from './components/signin/KyndrylSignInPage';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { KyndrylCatalogNode } from './components/catalog/KyndrylCatalogNode';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { HomePage } from './components/home/HomePage';
import { KyndrylNotFoundPage, KyndrylBootErrorPage } from './components/error/KyndrylErrorPages';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { UnifiedThemeProvider } from '@backstage/theme';
import { RunnerOpsContent } from './components/gitea/RunnerOpsContent';
import { CopilotAdminPage, CopilotExtensionMarketplace, CopilotGlobalPage } from './components/copilot';

const isWipVisible = () => {
  try {
    const flags = JSON.parse(localStorage.getItem('featureFlags') || '{}');
    return flags['show-wip-templates'] === 1;
  } catch {
    return false;
  }
};

const wipTemplateFilter = (entity: { metadata?: { tags?: string[] } }) => {
  if (isWipVisible()) return true;
  return !(entity?.metadata?.tags ?? []).includes('wip');
};

const createTemplateFilter = (groupName: string) => {
  return (entity: Entity) => {
    // First apply WIP filter
    if (!wipTemplateFilter(entity)) return false;

    const tags = entity?.metadata?.tags ?? [];
    const type = entity?.spec?.type;

    // platform-lead sees everything
    if (groupName === 'platform-lead') return true;
    // platform-engineering sees platform templates
    if (groupName === 'platform-engineering')
      return tags.includes('platform') || tags.includes('platform-admin');
    // demo-admins sees demo + test templates
    if (groupName === 'demo-admins')
      return tags.includes('demo') || type === 'test';
    // Default: demo-users and guests only see demo templates
    return tags.includes('demo');
  };
};

/**
 * Wrapper component that reads the active role from RoleContext and injects
 * an RBAC-based templateFilter. Re-renders when the user switches roles so
 * the template list updates immediately.
 */
const RbacScaffolderWrapper = ({ children }: { children: React.ReactElement }) => {
  const { activeRole, loading } = useRoleContext();

  if (loading) {
    return <Progress />;
  }

  const templateFilter = createTemplateFilter(activeRole?.groupName ?? '');

  return React.cloneElement(children, { templateFilter });
};



import { kyndrylDarkTheme, kyndrylLightTheme } from './theme/kyndrylTheme';

const app = createApp({
  apis,
  featureFlags: [{ name: 'show-wip-templates', pluginId: '' }],
  themes: [{
    id: 'kyndryl-dark',
    title: 'Kyndryl Dark',
    variant: 'dark',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={kyndrylDarkTheme} children={children} />
    ),
  }, {
    id: 'kyndryl-light',
    title: 'Kyndryl Light',
    variant: 'light',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={kyndrylLightTheme} children={children} />
    ),
  }],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(agentRegistryPlugin.externalRoutes, {
      toolsRegistry: toolsRegistryPlugin.routes.root,
      workflowComposer: workflowComposerPlugin.routes.root,
    });
    bind(toolsRegistryPlugin.externalRoutes, {
      agentRegistry: agentRegistryPlugin.routes.root,
      workflowComposer: workflowComposerPlugin.routes.root,
    });
    bind(workflowComposerPlugin.externalRoutes, {
      agentRegistry: agentRegistryPlugin.routes.root,
      toolsRegistry: toolsRegistryPlugin.routes.root,
    });
  },
  components: {
    SignInPage: props => <KyndrylSignInPage {...props} />,
    NotFoundErrorPage: KyndrylNotFoundPage,
    BootErrorPage: KyndrylBootErrorPage,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <RbacScaffolderWrapper>
          <ScaffolderPage
            templateFilter={wipTemplateFilter}
            groups={[
              {
                title: 'Golden Path & Demos',
                filter: entity => entity?.spec?.type === 'demo',
              },
              {
                title: 'Platform Services',
                filter: entity => entity?.spec?.type === 'service',
              },
              {
                title: 'Platform Administration',
                filter: entity =>
                  entity?.spec?.type === 'admin' ||
                  entity?.spec?.type === 'catalog' ||
                  entity?.spec?.type === 'resource',
              },
              {
                title: 'Test & Validation',
                filter: entity => entity?.spec?.type === 'test',
              },
            ]}
          >
            <ScaffolderFieldExtensions>
              <GiteaOrgPickerExtension />
              <HumanitecProjectPickerExtension />
              <UnregisteredAgentPickerExtension />
              <HelmValuesEditorExtension />
              <SystemOwnerFieldExtension />
              <ArgocdProjectPickerExtension />
            </ScaffolderFieldExtensions>
          </ScaffolderPage>
        </RbacScaffolderWrapper>
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout>
        <SettingsLayout.Route path="/general" title="General">
          <>
            <UserSettingsGeneral />
            <RefreshIntervalCard />
            <AgentRegistryProviderCard />
          </>
        </SettingsLayout.Route>
        <SettingsLayout.Route path="/auth-providers" title="Auth Providers">
          <UserSettingsAuthProviders />
        </SettingsLayout.Route>
        <SettingsLayout.Route path="/feature-flags" title="Feature Flags">
          <UserSettingsFeatureFlags />
        </SettingsLayout.Route>
      </SettingsLayout>
    </Route>
    <Route path="/ai-agent" element={<CopilotGlobalPage />} />
    <Route path="/platform-studio/*" element={<PlatformStudioPage />} />
    <Route path="/agent-registry/*" element={<AgentRegistryPage />} />
    <Route path="/tools-registry" element={<ToolsRegistryPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage renderNode={KyndrylCatalogNode} />} />
    <Route path="/workflow-composer" element={<WorkflowComposerPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/devtools" element={<DevToolsPage />} />
    <Route path="/catalog-unprocessed" element={<CatalogUnprocessedEntitiesPage />} />
    <Route path="/platform-ops/runner-health" element={<RunnerOpsContent />} />
    <Route path="/copilot-admin" element={<CopilotAdminPage />} />
    <Route path="/copilot-extensions" element={<CopilotExtensionMarketplace />} />
    <Route path="/quality-studio/*" element={<QualityStudioPage />} />
    {/* Required for the plugin routing system; the wizard is rendered via OnboardingGate, not this route */}
    <Route path="/onboarding" element={<OnboardingWizardPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <OnboardingGate>
        <RoleProvider>
          <Root>{routes}</Root>
        </RoleProvider>
      </OnboardingGate>
    </AppRouter>
  </>,
);
